import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled, { keyframes } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { ArrowIcon } from "./icon/ArrowIcon";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { COLOR_BLACK, COLOR_LIGHT_RED, COLOR_ORANGE } from "../../constants/color";

export type SelectOptionType = {
    id: number | string;
    name: string;
    value: any;
};

type Props = {
    name: string;
    options: SelectOptionType[];
    label?: string;
    placeholder?: string;
    required?: boolean;
    defaultValue?: any[]; // Array of values
    disabled?: boolean;
    onChange: (name: string, value: any[]) => void;
    clearable?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    width?: string;
    height?: string;
    hasSearchInput?: boolean;
};

export const MultiSelect: React.FC<Props> = ({
    name,
    label,
    placeholder = "--",
    required,
    defaultValue = [],
    options,
    disabled,
    onChange,
    clearable = true,
    hasError,
    errorMessage,
    width,
    height,
    hasSearchInput = true,
}) => {
    const uuid = useMemo(() => {
        return uuidv4();
    }, []);

    const [isOpen, setIsOpen] = useState<boolean | null>(null);

    // We store the selected VALUES in state
    const [selectedValues, setSelectedValues] = useState<any[]>(Array.isArray(defaultValue) ? defaultValue : []);

    const formContext = useFormContext();

    const handleOpenSelection = useCallback(() => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
    }, [disabled]);

    const handleChange = useCallback(
        (optionValue: any) => {
            // Toggle logic
            let newValues = [...selectedValues];
            // eslint-disable-next-line eqeqeq
            if (newValues.some(v => String(v) === String(optionValue))) {
                // eslint-disable-next-line eqeqeq
                newValues = newValues.filter(v => String(v) !== String(optionValue));
            } else {
                newValues.push(optionValue);
            }

            setSelectedValues(newValues);

            // Notify parent
            onChange(name, newValues);

            try {
                if (name && formContext) {
                    formContext.setValue(name, newValues, { shouldValidate: true });
                }
            } catch (err) { }
        },
        [selectedValues, formContext, name, onChange],
    );

    // Derive the display text from selectedValues
    const currentName = useMemo(() => {
        if (selectedValues.length === 0) return "";

        // Find names for all selected values
        const names = options
            .filter(opt => selectedValues.some(val => String(val) === String(opt.value)))
            .map(opt => opt.name);

        // Also include values that might not have matching options (edge case)
        selectedValues.forEach(val => {
            if (!options.some(opt => String(opt.value) === String(val))) {
                names.push(String(val));
            }
        });

        return names.join(", ");
    }, [selectedValues, options]);

    // Sync with defaultValue if it changes externally
    useEffect(() => {
        // Better: check if every default value is in selected and vice versa
        const defaultSet = new Set((Array.isArray(defaultValue) ? defaultValue : []).map(String));
        const currentSet = new Set(selectedValues.map(String));

        const setsEqual = defaultSet.size === currentSet.size &&
            [...defaultSet].every(value => currentSet.has(value));

        if (!setsEqual) {
            setSelectedValues(Array.isArray(defaultValue) ? defaultValue : []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    // Sync with form context if available
    useEffect(() => {
        if (!formContext) return;

        const subscription = formContext.watch((value, { name: fieldName }) => {
            // Only react if fieldName matches and value actually changed
            if (fieldName === name || !fieldName) { // !fieldName checks for reset
                const val = value[name];
                // Only update if array and different
                if (Array.isArray(val)) {
                    // check equality
                    const currentSet = new Set(selectedValues.map(String));
                    const newSet = new Set(val.map(String));
                    if (currentSet.size !== newSet.size || ![...currentSet].every(v => newSet.has(v))) {
                        setSelectedValues(val);
                    }
                } else if (!val) {
                    // Handle clear/reset
                    if (selectedValues.length > 0) setSelectedValues([]);
                }
            }
        });

        return () => subscription?.unsubscribe();
    }, [formContext, name, selectedValues]);


    const closeDropdown = useCallback((e: MouseEvent) => {

        // Keep open if clicking inside the dropdown container
        const target = e.target as HTMLElement;
        const container = document.getElementById(`container-${uuid}`);
        if (container && container.contains(target)) {
            return;
        }

        setIsOpen(false);
    }, [uuid]);

    useEffect(() => {
        if (isOpen) document.body.addEventListener("click", closeDropdown);
        else document.body.removeEventListener("click", closeDropdown);

        return () => {
            document.body.removeEventListener("click", closeDropdown);
        };
    }, [isOpen, closeDropdown]);

    const fillColor = useMemo(() => {
        if (disabled) return "#CCCCCC";
        if (hasError) return COLOR_ORANGE;
        return "#333333";
    }, [disabled, hasError]);

    return (
        <StyledContainer
            id={`container-${uuid}`}
            $disabled={disabled}
            $width={width || "100%"}
            $height={height}
        >
            <StyledHeader>
                {label && (
                    <StyledLabel>
                        {required ? (
                            <>
                                {label.replace(' *', '')} <StyledRequiredAsterisk>*</StyledRequiredAsterisk>
                            </>
                        ) : (
                            label
                        )}
                    </StyledLabel>
                )}
            </StyledHeader>

            <StyledSelect
                id={`select-container-${uuid}`}
                onClick={handleOpenSelection}
                $disabled={disabled}
                $hasError={hasError}
            >
                <StyledPlaceholder
                    id={`select-placeholder-${uuid}`}
                    $isPlaceholder={currentName === "" || currentName === placeholder}
                    title={currentName || placeholder}
                >
                    {currentName !== "" ? currentName : placeholder}
                </StyledPlaceholder>

                <StyledArrowIcon id={`select-dropdown-${uuid}`} $isOpen={isOpen}>
                    <ArrowIcon uuid={uuid} fillColor={fillColor} />
                </StyledArrowIcon>
            </StyledSelect>

            <MultiSelectDropdown
                isOpen={(isOpen ?? false) && !disabled}
                uuid={uuid}
                options={options}
                width={width || "100%"}
                selectedValues={selectedValues}
                handleChange={handleChange}
                hasSearchInput={hasSearchInput}
            />
        </StyledContainer>
    );
};

const StyledContainer = styled.div<{ $disabled?: boolean; $width: string; $height?: string }>`
  @media (max-width: 768px) {
    width: 100%;
  }

  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
  white-space: nowrap;
`;

const StyledRequiredAsterisk = styled.span`
  color: ${COLOR_ORANGE};
`;

const StyledSelect = styled.div<{
    $disabled?: boolean;
    $hasError?: boolean;
}>`
  padding: 10px 12px 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  outline: none;
  box-sizing: border-box;
  min-height: 44px;
  height: 100%;

  ${({ $disabled, $hasError }) =>
        `
      border-color: ${$disabled ? "#cccccc" : $hasError ? COLOR_ORANGE : "#cccccc"
        };
      background-color: ${$disabled ? "#f2f2f2" : $hasError ? COLOR_LIGHT_RED : "#ffffff"
        };
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
        $hasError ? COLOR_ORANGE : COLOR_BLACK};
  }
`;

const StyledPlaceholder = styled.div<{
    $isPlaceholder: boolean;
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: 170%;
  letter-spacing: 0.56px;
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? "#999999" : "#000000")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 30px);
  flex: 1;
  min-width: 0;
`;

const rotate = ($isOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  transform: ${({ $isOpen }) =>
        $isOpen === null
            ? "rotate(0deg)"
            : $isOpen
                ? "rotate(180deg)"
                : "rotate(0deg)"};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
`;
